import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fonction pour nettoyer les données utilisateur et éviter les références circulaires
  const cleanUserData = (user) => {
    if (!user) return null;
    
    return {
      _id: user._id,
      username: user.username,
      email: user.email,
      role: user.role,
      profileImage: user.profileImage,
      phone: user.phone,
      address: user.address,
      bio: user.bio,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt
    };
  };

  // Vérifier si l'utilisateur est déjà connecté au chargement de l'app
  useEffect(() => {
    const initAuth = () => {
      const user = authService.getCurrentUser();
      
      // Vérifier si l'utilisateur a tous les champs nécessaires
      if (user && (!user.role || !user._id)) {
        authService.logout();
        setCurrentUser(null);
      } else {
        const cleanUser = cleanUserData(user);
        setCurrentUser(cleanUser);
      }
      
      setLoading(false);
    };

    initAuth();
  }, []);

  // Connexion utilisateur
  const login = async (email, mdp) => {
    try {
      const response = await authService.login({ email, mdp });
      const cleanUser = cleanUserData(response.compte);
      setCurrentUser(cleanUser);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // 🔹 Connexion via Google
  const googleLogin = async (idToken) => {
    try {
      const response = await authService.googleLogin(idToken);
      const cleanUser = cleanUserData(response.compte);
      setCurrentUser(cleanUser);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Enregistrement d'un nouvel utilisateur
  const register = async (username, email, mdp, phone) => {
    try {
      const response = await authService.register({ username, email, mdp, phone });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Déconnexion
  const logout = () => {
    authService.logout();
    setCurrentUser(null);
  };

  // Récupérer le profil utilisateur
  const getProfile = async () => {
    try {
      console.log('Chargement du profil utilisateur...');
      const response = await authService.getProfile();
      console.log('Profil reçu:', response.user);
      const cleanUser = cleanUserData(response.user);
      setCurrentUser(cleanUser); // Utiliser response.user au lieu de response.data
      return response;
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
      throw error;
    }
  };

  // Mettre à jour le profil utilisateur
  const updateProfile = async (userData) => {
    try {
      const response = await authService.updateProfile(userData);
      const cleanUser = cleanUserData(response.data);
      setCurrentUser(cleanUser);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Valeur du contexte
  const value = {
    currentUser,
    loading,
    login,
    register,
    logout,
    getProfile,
    updateProfile,
    googleLogin, // 🔹 exposé ici
    // Méthode pour vérifier si l'utilisateur est admin
    isAdmin: () => {
      const result = currentUser && currentUser.role === 'admin';
      return result;
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  return useContext(AuthContext);
};
